import { getApiErrorMessage, getClient } from '@matix/api-client';
import type {
  CallCenterBootstrap,
  CallCenterDashboard,
  CallCenterOrderDetail,
  CallCenterOrderListResponse,
  CallCenterStatus,
  CategoryListResponse,
  Customer,
  CustomerSearchResponse,
  ItemListResponse,
  SearchResponse,
} from '@matix/types';

/**
 * Call Center API client.
 *
 * Mirrors pos-api/src/pos/call-center/callCenter.routes.ts. Uses the shared
 * axios instance (and therefore the same JWT, the same 401 redirect and the
 * same loader counter as the rest of the till) rather than a second client —
 * this is one more surface of the cashier app, not a separate application.
 *
 * Split into `agent` and `branch` on purpose, matching the two auth tiers on
 * the server: everything under `agent` needs the "Call Center" permission and
 * can reach any branch in the company; everything under `branch` is an
 * ordinary cashier action scoped to their own branch. Keeping them apart here
 * means a screen cannot casually reach for a call it is not entitled to.
 */

const BASE = '/api/v1/call-center';

function http() {
  return getClient().axios;
}

export interface QuoteResult {
  subtotal: number;
  discount: number;
  deliveryFee: number;
  taxPercentage: number;
  taxValue: number;
  /** Promotion/membership-EXCLUDED total — what goes on the wire as expected_total. */
  total: number;
  lines: Array<{ itemId: number; qty: number; price: number; total: number }>;
  /** Promotions + membership benefits the server priced for this basket. */
  engineDiscount: number;
  engineBreakdown: Array<{ name: string; amount: number }>;
  /** A coupon's value, priced by the server. Counts toward `total` too. */
  couponDiscount: number;
  couponCode: string | null;
  /** Why a supplied code was refused, so the agent can read it out. */
  couponError: string | null;
  /** What the customer actually pays. The number to read down the phone. */
  amountDue: number;
}

export interface CallCenterLinePayload {
  item_id: number;
  qty: number;
  price: number;
  discount_value?: number;
  notes?: string | null;
  modifiers?: Array<{
    option_id: number;
    group_id: number;
    group_name: string;
    name: string;
    name_ar?: string | null;
    price_delta: number;
  }>;
}

export interface CreateOrderPayload {
  idempotency_key: string;
  branch_id: number;
  customer_id: number;
  address_id?: number | null;
  driver_id?: number | null;
  order_type?: number | null;
  items: CallCenterLinePayload[];
  discount_value?: number;
  delivery_fee?: number;
  /** Promised delivery time as a naive local `YYYY-MM-DDTHH:mm:00`; omitted = now. */
  delivery_at?: string | null;
  note?: string | null;
  prepaid?: { payment_type_id: number } | null;
  /** Priced server-side; the code travels, never an amount. */
  coupon_code?: string | null;
  /** Must cover the order in full — a partial card is taken at hand-over. */
  gift_card?: { card_number: string; pin?: string | null } | null;
  expected_total: number;
}

export interface OrderListParams {
  status?: string;
  branch_id?: number;
  agent_user_id?: number;
  q?: string;
  from?: string;
  to?: string;
  open_only?: boolean;
  page?: number;
  limit?: number;
}

/**
 * The message to put in front of the agent when a call fails.
 *
 * Every refusal on this feature is a sentence written to be read down a phone
 * — "Discount of 48.34% exceeds your 0% limit", "This card holds 40.00, which
 * does not cover 73.50", "A delivery order needs a saved address". Reaching for
 * `err.message` on an axios rejection throws all of that away and shows
 * "Request failed with status code 403", which tells the agent nothing, tells
 * whoever they escalate to nothing, and turns a two-second fix into a support
 * call. The server's `{ error }` body is the message; this is how it gets out.
 */
export function ccErrorMessage(err: unknown, fallback: string): string {
  const message = getApiErrorMessage(err, fallback);
  // getApiErrorMessage falls back to the axios message before the caller's, so
  // a response with no readable body still leaks "Request failed with status
  // code N". The caller's sentence is better than that in every case.
  return /^Request failed with status code/i.test(message) ? fallback : message;
}

export const callCenterApi = {
  /**
   * Always safe to call, on any tenant, for any signed-in cashier — it answers
   * `provisioned: false` instead of erroring when migration 046 has not been
   * applied. That is what makes it usable as the sign-in probe that decides
   * which surface to open.
   */
  async bootstrap(): Promise<CallCenterBootstrap> {
    const { data } = await http().get<CallCenterBootstrap>(`${BASE}/bootstrap`);
    return data;
  },

  agent: {
    async categories(branchId: number): Promise<CategoryListResponse> {
      const { data } = await http().get<CategoryListResponse>(
        `${BASE}/branches/${branchId}/categories`,
      );
      return data;
    },
    async items(branchId: number, categoryId: number): Promise<ItemListResponse> {
      const { data } = await http().get<ItemListResponse>(
        `${BASE}/branches/${branchId}/categories/${categoryId}/items`,
      );
      return data;
    },
    async search(branchId: number, q: string, limit = 25): Promise<SearchResponse> {
      const { data } = await http().get<SearchResponse>(`${BASE}/branches/${branchId}/search`, {
        params: { q, limit },
      });
      return data;
    },
    async searchCustomers(q: string, limit = 20): Promise<CustomerSearchResponse> {
      const { data } = await http().get<CustomerSearchResponse>(`${BASE}/customers/search`, {
        params: { q, limit },
      });
      return data;
    },
    async getCustomer(id: number): Promise<Customer> {
      const { data } = await http().get<Customer>(`${BASE}/customers/${id}`);
      return data;
    },
    async quote(payload: {
      branch_id: number;
      items: CallCenterLinePayload[];
      discount_value?: number;
      delivery_fee?: number;
      /** Required for a membership benefit to be priced at all. */
      customer_id?: number | null;
      order_type?: number | null;
      coupon_code?: string | null;
    }): Promise<QuoteResult> {
      const { data } = await http().post<QuoteResult>(`${BASE}/orders/quote`, payload);
      return data;
    },
    /**
     * SEND ORDER.
     *
     * The 15s ceiling is the same reasoning as the till's: the request is
     * idempotent on `idempotency_key`, so giving up early is safe here and
     * nowhere else, and an agent watching a spinner for the shared 30s default
     * with a customer on the line is worse than a retry.
     */
    async create(payload: CreateOrderPayload): Promise<CallCenterOrderDetail> {
      const { data } = await http().post<CallCenterOrderDetail>(`${BASE}/orders`, payload, {
        timeout: 15_000,
      });
      return data;
    },
    async list(params: OrderListParams = {}): Promise<CallCenterOrderListResponse> {
      const { data } = await http().get<CallCenterOrderListResponse>(`${BASE}/orders`, { params });
      return data;
    },
    async get(id: number): Promise<CallCenterOrderDetail> {
      const { data } = await http().get<CallCenterOrderDetail>(`${BASE}/orders/${id}`);
      return data;
    },
    async cancel(id: number, reason: string | null): Promise<CallCenterOrderDetail> {
      const { data } = await http().post<CallCenterOrderDetail>(`${BASE}/orders/${id}/cancel`, {
        reason,
      });
      return data;
    },
    async setStatus(
      id: number,
      status: CallCenterStatus,
      note?: string | null,
    ): Promise<CallCenterOrderDetail> {
      const { data } = await http().post<CallCenterOrderDetail>(`${BASE}/orders/${id}/status`, {
        status,
        note: note ?? null,
      });
      return data;
    },
    async assignDriver(id: number, driverId: number | null): Promise<CallCenterOrderDetail> {
      const { data } = await http().post<CallCenterOrderDetail>(`${BASE}/orders/${id}/driver`, {
        driver_id: driverId,
      });
      return data;
    },
    async dashboard(params: { from?: string; to?: string; branch_id?: number } = {}) {
      const { data } = await http().get<CallCenterDashboard>(`${BASE}/dashboard`, { params });
      return data;
    },
  },

  branch: {
    async get(id: number): Promise<CallCenterOrderDetail> {
      const { data } = await http().get<CallCenterOrderDetail>(`${BASE}/branch/orders/${id}`);
      return data;
    },
    async pending(params: OrderListParams = {}): Promise<CallCenterOrderListResponse> {
      const { data } = await http().get<CallCenterOrderListResponse>(`${BASE}/branch/pending`, {
        params,
      });
      return data;
    },
    async dashboard(params: { from?: string; to?: string } = {}): Promise<CallCenterDashboard> {
      const { data } = await http().get<CallCenterDashboard>(`${BASE}/branch/dashboard`, {
        params,
      });
      return data;
    },
    /** Take the money at hand-over. See settleOrder in the service. */
    async settle(
      id: number,
      payments: Array<{ payment_type_id: number; amount: number }>,
      opts: {
        markDelivered?: boolean;
        /**
         * Cards making up part or all of the tender. Σ of these must equal Σ of
         * the splits on the tenant's gift-card payment code — the server
         * enforces it, for the same reason the order path does.
         */
        giftCards?: Array<{ card_number: string; pin?: string | null; amount: number }>;
      } = {},
    ): Promise<CallCenterOrderDetail> {
      const { data } = await http().post<CallCenterOrderDetail>(`${BASE}/orders/${id}/settle`, {
        payments,
        mark_delivered: opts.markDelivered !== false,
        ...(opts.giftCards && opts.giftCards.length > 0 ? { gift_cards: opts.giftCards } : {}),
      });
      return data;
    },
    async setStatus(
      id: number,
      status: CallCenterStatus,
      note?: string | null,
    ): Promise<CallCenterOrderDetail> {
      const { data } = await http().post<CallCenterOrderDetail>(
        `${BASE}/branch/orders/${id}/status`,
        { status, note: note ?? null },
      );
      return data;
    },
    /**
     * Send the order out: move it to `out_for_delivery` and, when one is named,
     * record who carried it — in one request. Deliberately not "assign driver"
     * plus "set status" from the screen; see `dispatchOrder` in the service.
     *
     * `driverId` may be null. The departure is what has to be recorded; naming
     * the driver is optional, and a null leaves any existing one alone.
     */
    async dispatch(
      id: number,
      driverId: number | null,
      note?: string | null,
    ): Promise<CallCenterOrderDetail> {
      const { data } = await http().post<CallCenterOrderDetail>(
        `${BASE}/branch/orders/${id}/dispatch`,
        { driver_id: driverId, note: note ?? null },
      );
      return data;
    },
  },
};

/**
 * The SSE URL for a live screen.
 *
 * The token rides in the query string because EventSource cannot set an
 * Authorization header — the same trade-off the KDS stream makes, documented at
 * the route. Returns null when there is no token, so a caller can simply not
 * open a stream rather than opening one that 401s in a reconnect loop.
 */
export function callCenterStreamUrl(mode: 'branch' | 'all'): string | null {
  const token = getClient().getToken() ?? null;
  if (!token) return null;
  const base = (import.meta.env.VITE_API_BASE_URL as string | undefined) ?? '';
  const params = new URLSearchParams({ mode, token });
  return `${base}${BASE}/stream?${params.toString()}`;
}
